import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import html2pdf from 'html2pdf.js'
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Spinner } from '../../../shared/components/ui/Spinner'
import { EmptyState } from '../../../shared/components/ui/EmptyState'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import { useAuth } from '../../../context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { useIssues, useStatuses, useIssueMutations, useSpace, useUpdateTimeTracking } from '../hooks'
import { IssueFilterBar, EMPTY_ISSUE_FILTERS, applyIssueFilters, type IssueFilters, type GroupByOption } from '../components/IssueFilterBar'
import { ListRow, LIST_GRID_CLASS } from '../components/ListRow'
import { SortableListRow } from '../components/SortableListRow'
import { ListBulkToolbar } from '../components/ListBulkToolbar'
import { NewIssueModal } from '../components/NewIssueModal'
import { IssueModal } from '../components/IssueModal'
import { TimeTrackingModal } from '../components/TimeTrackingModal'
import type { Issue } from '../api'
import { cn, cardClassName } from '@jira-clone/ui'

// Seed sample issues that match the JIRA-ZYNX screenshot 100%
const SAMPLE_ISSUES: Issue[] = [
  {
    id: 's-1',
    key: 'PROJ-1',
    title: 'Redesign onboarding flow mockups',
    type: 'task',
    status: 'in_progress',
    status_category: 'in_progress',
    priority: 'high',
    assignee_id: 'u-pp',
    created_at: '2026-07-22T20:25:00.000Z',
    updated_at: '2026-07-22T20:25:00.000Z',
    spaceId: '',
    due_date: '2026-08-05T00:00:00.000Z',
  },
  {
    id: 's-2',
    key: 'PROJ-2',
    title: 'Create icon set for mobile app v2',
    type: 'task',
    status: 'todo',
    status_category: 'todo',
    priority: 'medium',
    assignee_id: 'u-pp',
    created_at: '2026-07-22T20:25:00.000Z',
    updated_at: '2026-07-22T20:25:00.000Z',
    spaceId: '',
    due_date: '2026-07-29T00:00:00.000Z',
  },
  {
    id: 's-3',
    key: 'PROJ-3',
    title: 'Review accessibility contrast on landing page',
    type: 'task',
    status: 'todo',
    status_category: 'todo',
    priority: 'high',
    assignee_id: 'u-jl',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: '2026-09-22T00:00:00.000Z',
  },
  {
    id: 's-4',
    key: 'PROJ-4',
    title: 'Update component library in design system',
    type: 'task',
    status: 'in_progress',
    status_category: 'in_progress',
    priority: 'medium',
    assignee_id: 'u-pp',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: null,
  },
  {
    id: 's-5',
    key: 'PROJ-5',
    title: 'Prepare assets for client presentation',
    type: 'task',
    status: 'done',
    status_category: 'done',
    priority: 'high',
    assignee_id: 'u-jl',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: null,
  },
  {
    id: 's-6',
    key: 'PROJ-6',
    title: 'Design empty states for dashboard',
    type: 'task',
    status: 'todo',
    status_category: 'todo',
    priority: 'low',
    assignee_id: 'u-pp',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: null,
  },
  {
    id: 's-7',
    key: 'PROJ-7',
    title: 'Conduct usability test on checkout flow',
    type: 'task',
    status: 'in_progress',
    status_category: 'in_progress',
    priority: 'medium',
    assignee_id: 'u-jl',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: '2026-09-28T00:00:00.000Z',
  },
  {
    id: 's-8',
    key: 'PROJ-8',
    title: 'Create illustration set for marketing site',
    type: 'task',
    status: 'todo',
    status_category: 'todo',
    priority: 'medium',
    assignee_id: 'u-jl',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: '2026-10-08T00:00:00.000Z',
  },
  {
    id: 's-8-sub',
    key: 'PROJ-8-1',
    title: 'Illustrate header banner assets',
    type: 'subtask',
    status: 'todo',
    status_category: 'todo',
    priority: 'medium',
    assignee_id: 'u-jl',
    parent_id: 's-8',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: '2026-10-08T00:00:00.000Z',
  },
  {
    id: 's-9',
    key: 'PROJ-9',
    title: 'Audit design system for inconsistent spacing',
    type: 'task',
    status: 'done',
    status_category: 'done',
    priority: 'low',
    assignee_id: 'u-pp',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    spaceId: '',
    due_date: null,
  },
]

export const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  type: 64,
  work: 280,
  labels: 140,
  assignee: 140,
  reporter: 130,
  priority: 100,
  status: 120,
  updated: 180,
  dueDate: 140,
  created: 110,
}

export const MIN_COLUMN_WIDTHS: Record<string, number> = {
  type: 48,
  work: 160,
  labels: 70,
  assignee: 80,
  reporter: 80,
  priority: 70,
  status: 80,
  updated: 110,
  dueDate: 90,
  created: 80,
}

interface ColumnResizeHandleProps {
  onResizeStart: (e: React.MouseEvent) => void
  onDoubleClick?: () => void
  isResizing?: boolean
}

export function ColumnResizeHandle({
  onResizeStart,
  onDoubleClick,
  isResizing,
}: ColumnResizeHandleProps) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize column (double-click to reset)"
      onMouseDown={onResizeStart}
      onDoubleClick={(e) => {
        e.stopPropagation()
        onDoubleClick?.()
      }}
      className={`absolute top-0 right-0 h-full w-3.5 -mr-[7px] cursor-col-resize select-none z-20 flex items-center justify-center group/resizer ${
        isResizing ? 'cursor-col-resize' : ''
      }`}
    >
      <div
        className={`w-[1px] transition-all duration-150 ${
          isResizing
            ? 'h-full w-[2px] bg-blue-500 shadow-xs'
            : 'h-4 bg-[#DCDFE4] dark:bg-slate-700 group-hover/resizer:h-full group-hover/resizer:w-[2px] group-hover/resizer:bg-blue-500'
        }`}
      />
    </div>
  )
}

export function ListPage() {
  const { id: routeSpaceId } = useParams<{ id: string }>()
  const spaceId = routeSpaceId || ''
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedFromUrl = searchParams.get('selectedIssue')
  const { user } = useAuth()

  const { data: rawIssues = [], isLoading } = useIssues(spaceId || null, { onlyMine: true })
  const { data: statuses = [] } = useStatuses(spaceId || null)
  const { data: allUsers = [] } = useUserOptions(spaceId || null)
  const { data: space } = useSpace(spaceId || null)
  const { update, remove } = useIssueMutations(spaceId || null)

  const [newIssueOpen, setNewIssueOpen] = useState(false)
  const [openIssueId, setOpenIssueId] = useState<string | null>(selectedFromUrl)
  const [filters, setFilters] = useState<IssueFilters>(EMPTY_ISSUE_FILTERS)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const [localIssuesOrder, setLocalIssuesOrder] = useState<string[]>([])
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('goodday_list_col_widths')
      if (saved) {
        const parsed = JSON.parse(saved)
        return { ...DEFAULT_COLUMN_WIDTHS, ...parsed }
      }
    } catch {
      // fallback
    }
    return DEFAULT_COLUMN_WIDTHS
  })

  const [resizingCol, setResizingCol] = useState<string | null>(null)

  useEffect(() => {
    try {
      localStorage.setItem('goodday_list_col_widths', JSON.stringify(columnWidths))
    } catch {
      // ignore
    }
  }, [columnWidths])

  const handleResizeStart = (colKey: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setResizingCol(colKey)

    const startX = e.clientX
    const startWidth = columnWidths[colKey] || DEFAULT_COLUMN_WIDTHS[colKey] || 100
    const minWidth = MIN_COLUMN_WIDTHS[colKey] || 50

    const onMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault()
      const delta = moveEvent.clientX - startX
      const nextWidth = Math.max(minWidth, Math.round(startWidth + delta))
      setColumnWidths((prev) => ({
        ...prev,
        [colKey]: nextWidth,
      }))
    }

    const onMouseUp = () => {
      setResizingCol(null)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const handleResetWidth = (colKey: string) => {
    setColumnWidths((prev) => ({
      ...prev,
      [colKey]: DEFAULT_COLUMN_WIDTHS[colKey] || 100,
    }))
  }

  const [visibleColumns, setVisibleColumns] = useState({
    type: true,
    work: true,
    labels: true,
    assignee: true,
    reporter: true,
    priority: true,
    status: true,
    updated: true,
    dueDate: true,
    created: false,
  })

  useEffect(() => {
    if (selectedFromUrl !== openIssueId) {
      setOpenIssueId(selectedFromUrl)
    }
  }, [selectedFromUrl])

  const handleOpenIssue = (id: string) => {
    setOpenIssueId(id)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', id)
      return next
    })
  }

  const handleCloseIssue = () => {
    setOpenIssueId(null)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedIssue')
      return next
    })
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  // Check if current user is superadmin / owner
  const isSuperAdmin = Boolean(
    (user?.role && isSuperAdminOrOwner(user.role)) ||
    user?.department?.toLowerCase() === 'superadmin'
  )

  // Real issues from backend (strictly filtered to current user's own tasks for employee roles)
  const issues: Issue[] = useMemo(() => {
    if (!rawIssues || rawIssues.length === 0) return []
    const list = rawIssues as Issue[]
    if (isSuperAdmin) return list
    if (!user?.id) return list

    const currentUserId = String(user.id)
    const currentUsername = user.username ? String(user.username) : ''

    return list.filter((issue: any) => {
      const singleAssignee = issue.assignee_id ? String(issue.assignee_id) : (issue.assignee ? String(typeof issue.assignee === 'object' ? issue.assignee._id || issue.assignee.id : issue.assignee) : '')
      const assignees = Array.isArray(issue.assigneeIds)
        ? issue.assigneeIds.map((a: any) => (typeof a === 'object' ? String(a._id || a.id) : String(a)))
        : []

      const isAssigned = (
        singleAssignee === currentUserId ||
        (currentUsername && singleAssignee === currentUsername) ||
        assignees.includes(currentUserId) ||
        (currentUsername && assignees.includes(currentUsername))
      )
      if (isAssigned) return true

      // Also retain parent task if any of its subtasks are assigned to the user
      const hasAssignedChild = list.some((child: any) => {
        const pId = child.parent_id || child.parentId
        if (pId !== issue.id) return false
        const childAssignee = child.assignee_id ? String(child.assignee_id) : (child.assignee ? String(typeof child.assignee === 'object' ? child.assignee._id || child.assignee.id : child.assignee) : '')
        const childAssignees = Array.isArray(child.assigneeIds)
          ? child.assigneeIds.map((a: any) => (typeof a === 'object' ? String(a._id || a.id) : String(a)))
          : []
        return (
          childAssignee === currentUserId ||
          (currentUsername && childAssignee === currentUsername) ||
          childAssignees.includes(currentUserId) ||
          (currentUsername && childAssignees.includes(currentUsername))
        )
      })

      return hasAssignedChild
    })
  }, [rawIssues, isSuperAdmin, user?.id, user?.username])

  // Subtasks group map
  const subtasksByParent = useMemo(() => {
    const map = new Map<string, Issue[]>()
    for (const issue of issues) {
      if (!issue.parent_id) continue
      const list = map.get(issue.parent_id) ?? []
      list.push(issue)
      map.set(issue.parent_id, list)
    }
    return map
  }, [issues])

  // Subtask done counts per parent
  const subtaskDoneCount = useMemo(() => {
    const map = new Map<string, number>()
    for (const issue of issues) {
      if (!issue.parent_id) continue
      if (issue.status_category === 'done') {
        map.set(issue.parent_id, (map.get(issue.parent_id) ?? 0) + 1)
      }
    }
    return map
  }, [issues])

  // Top level issues (not subtasks)
  const topLevelIssues = useMemo(() => {
    const list = issues.filter((i) => !i.parent_id)
    if (localIssuesOrder.length > 0) {
      return [...list].sort((a, b) => {
        const idxA = localIssuesOrder.indexOf(a.id)
        const idxB = localIssuesOrder.indexOf(b.id)
        if (idxA !== -1 && idxB !== -1) return idxA - idxB
        return 0
      })
    }
    return list
  }, [issues, localIssuesOrder])

  // Filtered issues
  const filteredTopLevel = useMemo(() => {
    return applyIssueFilters(topLevelIssues, filters, (assigneeId) => assigneeId ?? '')
  }, [topLevelIssues, filters])

  // Group issues by selected groupBy option
  const groupedIssues = useMemo(() => {
    if (filters.groupBy === 'none') {
      return [{ groupKey: 'all', groupLabel: 'All Issues', issues: filteredTopLevel }]
    }

    const groups = new Map<string, { groupKey: string; groupLabel: string; issues: Issue[] }>()

    for (const issue of filteredTopLevel) {
      let groupKey = 'none'
      let groupLabel = 'Ungrouped'

      switch (filters.groupBy) {
        case 'status': {
          const status = statuses.find((s) => s.id === issue.status)
          groupKey = issue.status
          groupLabel = status?.name || issue.status
          break
        }
        case 'assignee': {
          const assignee = allUsers.find((u) => u.id === issue.assignee_id)
          groupKey = issue.assignee_id || 'unassigned'
          groupLabel = assignee?.display_name || (issue.assignee_id ? 'Unknown' : 'Unassigned')
          break
        }
        case 'priority': {
          groupKey = issue.priority || 'none'
          const priorityLabels: Record<string, string> = {
            highest: 'Highest',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
            lowest: 'Lowest',
            none: 'No Priority',
          }
          groupLabel = priorityLabels[issue.priority || 'none'] || 'No Priority'
          break
        }
        case 'type': {
          groupKey = issue.type
          const typeLabels: Record<string, string> = {
            epic: 'Epic',
            story: 'Story',
            task: 'Task',
            bug: 'Bug',
            subtask: 'Sub-task',
          }
          groupLabel = typeLabels[issue.type] || issue.type
          break
        }
        case 'sprint': {
          groupKey = issue.sprint_id || 'backlog'
          groupLabel = issue.sprint_id ? `Sprint ${issue.sprint_id}` : 'Backlog'
          break
        }
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, { groupKey, groupLabel, issues: [] })
      }
      groups.get(groupKey)!.issues.push(issue)
    }

    // Sort groups based on groupBy type
    const groupArray = Array.from(groups.values())
    if (filters.groupBy === 'status') {
      // Sort by status order
      const statusOrder = new Map(statuses.map((s, i) => [s.id, i]))
      groupArray.sort((a, b) => (statusOrder.get(a.groupKey) ?? 999) - (statusOrder.get(b.groupKey) ?? 999))
    } else if (filters.groupBy === 'priority') {
      const priorityOrder = { highest: 0, high: 1, medium: 2, low: 3, lowest: 4, none: 5 }
      groupArray.sort((a, b) => (priorityOrder[a.groupKey as keyof typeof priorityOrder] ?? 5) - (priorityOrder[b.groupKey as keyof typeof priorityOrder] ?? 5))
    } else if (filters.groupBy === 'type') {
      const typeOrder = { epic: 0, story: 1, task: 2, bug: 3, subtask: 4 }
      groupArray.sort((a, b) => (typeOrder[a.groupKey as keyof typeof typeOrder] ?? 5) - (typeOrder[b.groupKey as keyof typeof typeOrder] ?? 5))
    }

    return groupArray
  }, [filteredTopLevel, filters.groupBy, statuses, allUsers])

  // Flatten visible order for Shift+Click range selection (for grouped, flatten all groups)
  const flatVisibleIssues = useMemo(() => {
    if (filters.groupBy === 'none') return filteredTopLevel
    return groupedIssues.flatMap((g) => g.issues)
  }, [filteredTopLevel, groupedIssues, filters.groupBy])

  const toggleSelect = (id: string, checked: boolean, shiftKey = false) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (shiftKey && lastSelectedId) {
        const lastIdx = flatVisibleIssues.findIndex((i) => i.id === lastSelectedId)
        const curIdx = flatVisibleIssues.findIndex((i) => i.id === id)
        if (lastIdx !== -1 && curIdx !== -1) {
          const [start, end] = lastIdx < curIdx ? [lastIdx, curIdx] : [curIdx, lastIdx]
          if (checked) {
            for (let i = start; i <= end; i++) next.add(flatVisibleIssues[i].id)
          } else {
            for (let i = start; i <= end; i++) next.delete(flatVisibleIssues[i].id)
          }
          return next
        }
      }
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
    setLastSelectedId(id)
  }

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = filteredTopLevel.findIndex((i) => i.id === active.id)
    const newIndex = filteredTopLevel.findIndex((i) => i.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const newOrder = [...filteredTopLevel]
    const [moved] = newOrder.splice(oldIndex, 1)
    newOrder.splice(newIndex, 0, moved)

    setLocalIssuesOrder(newOrder.map((i) => i.id))
    toast.success('Order updated')
  }

  const handleUpdate = async (id: string, payload: Partial<Issue>) => {
    try {
      await update.mutateAsync({ id, payload })
      toast.success('Issue updated')
    } catch {
      toast.error('Could not update issue')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await remove.mutateAsync(id)
      toast.success('Issue deleted')
    } catch {
      toast.error('Could not delete issue')
    }
  }

  const runBulk = async (fn: (id: string) => Promise<unknown>, successMessage: string) => {
    setBulkBusy(true)
    try {
      await Promise.all(Array.from(selected).map(fn))
      toast.success(successMessage)
      setSelected(new Set())
    } catch {
      toast.error('Bulk update failed')
    } finally {
      setBulkBusy(false)
    }
  }

  const handleMoveOnList = async (issueId: string, statusId: string) => {
    const target = statuses.find((s) => s.id === statusId)
    await handleUpdate(issueId, { status: statusId, status_category: target?.category || 'todo' })
  }

  // Time tracking
  const [timeTrackingOpen, setTimeTrackingOpen] = useState(false)
  const [timeTrackingIssueId, setTimeTrackingIssueId] = useState<string | null>(null)

  const handleLogTime = (issueId: string) => {
    setTimeTrackingIssueId(issueId)
    setTimeTrackingOpen(true)
  }

  const handleSaveTimeTracking = async (timeSpent: number, timeRemaining: number | null) => {
    if (!timeTrackingIssueId) return
    try {
      await update.mutateAsync({ id: timeTrackingIssueId, payload: { timeSpent, timeRemaining } })
      toast.success('Time tracking updated')
    } catch {
      toast.error('Could not update time tracking')
    }
  }

  // ── Export handlers ────────────────────────────────────────────────────────

  /** Flatten grouped issues into a plain array for export */
  const getExportIssues = () => {
    if (filters.groupBy === 'none') return filteredTopLevel
    return groupedIssues.flatMap((g) => g.issues)
  }

  const getMemberName = (id?: string | null) => {
    if (!id) return '—'
    return allUsers.find((u) => u.id === id)?.display_name || id
  }

  const getStatusName = (id: string) => statuses.find((s) => s.id === id)?.name || id

  const typeLabel: Record<string, string> = { epic: 'Epic', story: 'Story', task: 'Task', bug: 'Bug', subtask: 'Sub-task' }

  const handleExportExcel = () => {
    const issues = getExportIssues()
    if (issues.length === 0) {
      toast.error('No issues to export')
      return
    }

    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`
    const header = ['Key', 'Title', 'Type', 'Status', 'Priority', 'Assignee', 'Due Date', 'Updated']
    const rows = issues.map((issue) => [
      issue.key || '',
      issue.title,
      typeLabel[issue.type] || issue.type,
      getStatusName(issue.status),
      issue.priority || '',
      getMemberName(issue.assignee_id),
      issue.due_date || '',
      issue.updated_at ? new Date(issue.updated_at).toLocaleDateString() : '',
    ])

    const csv = '\uFEFF' + [header, ...rows].map((row) => row.map(esc).join(',')).join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `issues-export-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(`Exported ${issues.length} issue(s) to Excel`)
  }

  const handleExportPdf = () => {
    const issues = getExportIssues()
    if (issues.length === 0) {
      toast.error('No issues to export')
      return
    }

    const container = document.createElement('div')
    container.id = 'pdf-export-temp'
    Object.assign(container.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '99999',
      background: '#fff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '12px',
      color: '#172B4D',
      padding: '24px 32px',
      overflow: 'auto',
    })

    const title = document.createElement('h2')
    title.textContent = `Issues — ${new Date().toLocaleDateString()}`
    Object.assign(title.style, { marginBottom: '16px', fontSize: '18px', fontWeight: 700 })
    container.appendChild(title)

    const table = document.createElement('table')
    table.style.cssText = 'width:100%;border-collapse:collapse;'

    const thead = document.createElement('thead')
    thead.innerHTML = `
      <tr style="background:#F4F5F7;">
        ${['Key','Title','Type','Status','Priority','Assignee','Due Date','Updated'].map((h) => `<th style="padding:8px 10px;text-align:left;font-size:11px;font-weight:600;color:#5E6C84;border-bottom:2px solid #DFE1E6;text-transform:uppercase;letter-spacing:0.04em;">${h}</th>`).join('')}
      </tr>`
    table.appendChild(thead)

    const tbody = document.createElement('tbody')
    for (const issue of issues) {
      const row = document.createElement('tr')
      row.style.borderBottom = '1px solid #EBECF0'
      row.innerHTML = `
        <td style="padding:7px 10px;font-weight:600;color:#0052CC;">${issue.key || ''}</td>
        <td style="padding:7px 10px;">${issue.title}</td>
        <td style="padding:7px 10px;">${typeLabel[issue.type] || issue.type}</td>
        <td style="padding:7px 10px;">${getStatusName(issue.status)}</td>
        <td style="padding:7px 10px;">${issue.priority || '—'}</td>
        <td style="padding:7px 10px;">${getMemberName(issue.assignee_id)}</td>
        <td style="padding:7px 10px;">${issue.due_date ? new Date(issue.due_date).toLocaleDateString() : '—'}</td>
        <td style="padding:7px 10px;">${issue.updated_at ? new Date(issue.updated_at).toLocaleDateString() : '—'}</td>
      `
      tbody.appendChild(row)
    }
    table.appendChild(tbody)
    container.appendChild(table)

    document.body.appendChild(container)

    // Use requestAnimationFrame so the browser paints the element before html2canvas captures it.
    requestAnimationFrame(() => {
      html2pdf()
        .set({
          margin: [12, 12, 12, 12],
          filename: `issues-export-${new Date().toISOString().slice(0, 10)}.pdf`,
          image: { type: 'jpeg', quality: 0.96 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
          ...( { pagebreak: { mode: ['avoid-all', 'css', 'legacy'] } } as Record<string, unknown> ),
        })
        .from(container)
        .save()
        .then(() => {
          if (document.body.contains(container)) document.body.removeChild(container)
          toast.success(`Exported ${issues.length} issue(s) to PDF`)
        })
        .catch(() => {
          if (document.body.contains(container)) document.body.removeChild(container)
          toast.error('PDF export failed')
        })
    })
  }

  const handleExport = (format: 'excel' | 'pdf') => {
    if (format === 'excel') handleExportExcel()
    else handleExportPdf()
  }

  // Keyboard shortcuts: C=create, Escape=close modal, Shift+Up/Down=range select
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't fire when typing in inputs/selects/textareas
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return

      // C — create issue
      if (e.key === 'c' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setNewIssueOpen(true)
        return
      }

      // Escape — close modals or clear selection
      if (e.key === 'Escape') {
        if (selected.size > 0) {
          e.preventDefault()
          setSelected(new Set())
          return
        }
      }

  // Arrow keys navigate rows when table is focused
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    if (filteredTopLevel.length === 0) return
    e.preventDefault()
    setSelected((prev) => {
      const prevArr = Array.from(prev)
      const current = prevArr.find((id) => filteredTopLevel.some((i) => i.id === id))
      const idx = current
        ? filteredTopLevel.findIndex((i) => i.id === current)
        : -1
      const delta = e.key === 'ArrowDown' ? 1 : -1
      const nextIdx = Math.max(0, Math.min(filteredTopLevel.length - 1, idx + delta))
      const nextId = filteredTopLevel[nextIdx].id
      setLastSelectedId(nextId)
      return new Set([nextId])
    })
  }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selected.size, filteredTopLevel])

  const targetIssueId = openIssueId || selectedFromUrl || null

  const activeIssue = useMemo(() => {
    if (!targetIssueId) return null

    // 1. Search current loaded issues (by id or key)
    const foundInIssues = issues.find(
      (i) => i.id === targetIssueId || i.key?.toLowerCase() === targetIssueId.toLowerCase(),
    )
    if (foundInIssues) return foundInIssues

    // 2. Search SAMPLE_ISSUES (e.g. s-1, PROJ-1)
    const foundInSample = SAMPLE_ISSUES.find(
      (i) => i.id === targetIssueId || i.key?.toLowerCase() === targetIssueId.toLowerCase(),
    )
    if (foundInSample) return { ...foundInSample, spaceId: spaceId ?? '' }

    // 3. Search rawIssues
    const foundInRaw = (rawIssues as Issue[]).find(
      (i) => i.id === targetIssueId || i.key?.toLowerCase() === targetIssueId.toLowerCase(),
    )
    if (foundInRaw) return foundInRaw

    // 4. Construct valid fallback issue so modal never fails to open
    return {
      id: targetIssueId,
      key:
        targetIssueId.toUpperCase().startsWith('PROJ') || targetIssueId.toUpperCase().startsWith('TASK')
          ? targetIssueId.toUpperCase()
          : `TASK-${targetIssueId.slice(-4).toUpperCase()}`,
      title: `Issue ${targetIssueId}`,
      type: 'task' as const,
      status: 'todo',
      status_category: 'todo' as const,
      priority: 'medium' as const,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      spaceId: spaceId ?? '',
    }
  }, [targetIssueId, issues, rawIssues, spaceId])

  return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-950 font-sans">
      <div className="flex min-h-0 flex-1 flex-col p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Header: title */}
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-slate-200">
                Issues
                {rawIssues.length > 0 && (
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-slate-800 dark:text-slate-400">
                    {filteredTopLevel.length}
                  </span>
                )}
              </h2>
            </div>

            {/* Filter bar */}
            <IssueFilterBar
              filters={filters}
              onChange={setFilters}
              members={allUsers}
              statuses={statuses.map((s) => ({ id: s.id, name: s.name, color: s.color }))}
              sprints={[]}
              onExport={handleExport}
            />

            {filteredTopLevel.length === 0 ? (
              <EmptyState
                title={issues.length === 0 ? 'No issues yet' : 'No issues match these filters'}
                description={issues.length === 0 ? 'Create the first issue for this project.' : 'Try clearing a filter.'}
              />
            ) : (
              <div className={cn(cardClassName(), 'min-h-0 flex-1 overflow-auto')}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={flatVisibleIssues.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    <table className="w-max min-w-full border-collapse text-left table-fixed">
                      {/* Table Header */}
                      <thead className="sticky top-0 z-10 bg-white border-b border-[#EBECF0] text-xs font-normal text-[#5E6C84] dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400">
                    <tr>
                      {/* 1. Select all Checkbox */}
                      <th className="w-10 min-w-[40px] max-w-[40px] pl-4 pr-1 py-3 text-center align-middle font-normal relative">
                        <input
                          type="checkbox"
                          checked={selected.size > 0 && filteredTopLevel.every((i) => selected.has(i.id))}
                          onChange={(e) =>
                            setSelected(e.target.checked ? new Set(filteredTopLevel.map((i) => i.id)) : new Set())
                          }
                          className="h-4 w-4 rounded-[3px] border border-[#C1C7D0] text-[#0052CC] focus:ring-0 focus:outline-none cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-600"
                        />
                      </th>

                      {/* 2. Type */}
                      {visibleColumns.type && (
                        <th
                          style={{
                            width: `${columnWidths.type}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.type}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 select-none group/th"
                        >
                          <span>Type</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'type'}
                            onResizeStart={(e) => handleResizeStart('type', e)}
                            onDoubleClick={() => handleResetWidth('type')}
                          />
                        </th>
                      )}

                      {/* 3. Work */}
                      {visibleColumns.work && (
                        <th
                          style={{
                            width: `${columnWidths.work}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.work}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 select-none group/th"
                        >
                          <span>Work</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'work'}
                            onResizeStart={(e) => handleResizeStart('work', e)}
                            onDoubleClick={() => handleResetWidth('work')}
                          />
                        </th>
                      )}

                      {/* 4. Labels */}
                      {visibleColumns.labels && (
                        <th
                          style={{
                            width: `${columnWidths.labels}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.labels}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 hidden lg:table-cell select-none group/th"
                        >
                          <span>Labels</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'labels'}
                            onResizeStart={(e) => handleResizeStart('labels', e)}
                            onDoubleClick={() => handleResetWidth('labels')}
                          />
                        </th>
                      )}

                      {/* 5. Assignee */}
                      {visibleColumns.assignee && (
                        <th
                          style={{
                            width: `${columnWidths.assignee}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.assignee}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 select-none group/th"
                        >
                          <span>Assignee</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'assignee'}
                            onResizeStart={(e) => handleResizeStart('assignee', e)}
                            onDoubleClick={() => handleResetWidth('assignee')}
                          />
                        </th>
                      )}

                      {/* 6. Reporter */}
                      {visibleColumns.reporter && (
                        <th
                          style={{
                            width: `${columnWidths.reporter}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.reporter}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 select-none group/th"
                        >
                          <span>Reporter</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'reporter'}
                            onResizeStart={(e) => handleResizeStart('reporter', e)}
                            onDoubleClick={() => handleResetWidth('reporter')}
                          />
                        </th>
                      )}

                      {/* 7. Priority */}
                      {visibleColumns.priority && (
                        <th
                          style={{
                            width: `${columnWidths.priority}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.priority}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 select-none group/th"
                        >
                          <span>Priority</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'priority'}
                            onResizeStart={(e) => handleResizeStart('priority', e)}
                            onDoubleClick={() => handleResetWidth('priority')}
                          />
                        </th>
                      )}

                      {/* 8. Status */}
                      {visibleColumns.status && (
                        <th
                          style={{
                            width: `${columnWidths.status}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.status}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 select-none group/th"
                        >
                          <span>Status</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'status'}
                            onResizeStart={(e) => handleResizeStart('status', e)}
                            onDoubleClick={() => handleResetWidth('status')}
                          />
                        </th>
                      )}

                      {/* 9. Updated */}
                      {visibleColumns.updated && (
                        <th
                          style={{
                            width: `${columnWidths.updated}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.updated}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 border-r border-[#EBECF0] dark:border-slate-800 select-none group/th"
                        >
                          <span>Updated</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'updated'}
                            onResizeStart={(e) => handleResizeStart('updated', e)}
                            onDoubleClick={() => handleResetWidth('updated')}
                          />
                        </th>
                      )}

                      {/* 10. Due date */}
                      {visibleColumns.dueDate && (
                        <th
                          style={{
                            width: `${columnWidths.dueDate}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.dueDate}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 select-none group/th"
                        >
                          <span>Due date</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'dueDate'}
                            onResizeStart={(e) => handleResizeStart('dueDate', e)}
                            onDoubleClick={() => handleResetWidth('dueDate')}
                          />
                        </th>
                      )}

                      {/* 11. Created */}
                      {visibleColumns.created && (
                        <th
                          style={{
                            width: `${columnWidths.created}px`,
                            minWidth: `${MIN_COLUMN_WIDTHS.created}px`,
                          }}
                          className="relative px-3 py-3 text-left text-xs font-normal text-[#5E6C84] dark:text-slate-400 hidden md:table-cell select-none group/th"
                        >
                          <span>Created</span>
                          <ColumnResizeHandle
                            isResizing={resizingCol === 'created'}
                            onResizeStart={(e) => handleResizeStart('created', e)}
                            onDoubleClick={() => handleResetWidth('created')}
                          />
                        </th>
                      )}

                      {/* 12. Actions */}
                      <th className="w-[44px] min-w-[44px] max-w-[44px] px-2 py-3 text-center" />
                    </tr>
                  </thead>

                  {/* Table Rows (Sortable) - Grouped or flat */}
                  <tbody className="bg-white dark:bg-slate-900">
                        {groupedIssues.map((group) => (
                          <React.Fragment key={group.groupKey}>
                            {/* Group Header Row */}
                            {filters.groupBy !== 'none' && (
                              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-[#EBECF0] dark:border-slate-800">
                                <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 2} className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#172B4D] dark:text-slate-200 uppercase tracking-wider">
                                      {group.groupLabel}
                                    </span>
                                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700 text-xs font-medium text-slate-600 dark:text-slate-300">
                                      {group.issues.length}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            )}
                            {group.issues.map((issue) => {
                              const subtasks = subtasksByParent.get(issue.id) ?? []
                              const isExpanded = expanded.has(issue.id)
                              return (
                                <React.Fragment key={issue.id}>
                                  <SortableListRow
                                    issue={issue}
                                    statuses={statuses}
                                    members={allUsers}
                                    selected={selected.has(issue.id)}
                                    onToggleSelect={(checked) => toggleSelect(issue.id, checked)}
                                    onOpen={() => handleOpenIssue(issue.id)}
                                    onUpdate={(payload) => handleUpdate(issue.id, payload)}
                                    onDelete={() => handleDelete(issue.id)}
                                    onLogTime={handleLogTime}
                                    allStatuses={statuses}
                                    subtaskCount={subtasks.length}
                                    subtaskDoneCount={subtaskDoneCount.get(issue.id) ?? 0}
                                    expanded={isExpanded}
                                    onToggleExpand={() => toggleExpand(issue.id)}
                                    visibleColumns={visibleColumns}
                                    columnWidths={columnWidths}
                                  />
                                  {isExpanded &&
                                    subtasks.map((subtask) => (
                                      <ListRow
                                        key={subtask.id}
                                        issue={subtask}
                                        statuses={statuses}
                                        members={allUsers}
                                        selected={selected.has(subtask.id)}
                                        onToggleSelect={(checked) => toggleSelect(subtask.id, checked)}
                                        onOpen={() => handleOpenIssue(subtask.id)}
                                        onUpdate={(payload) => handleUpdate(subtask.id, payload)}
                                        indent
                                        visibleColumns={visibleColumns}
                                        columnWidths={columnWidths}
                                      />
                                    ))}
                                </React.Fragment>
                              )
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bulk actions bar */}
      <ListBulkToolbar
        selectedCount={selected.size}
        totalCount={filteredTopLevel.length}
        statuses={statuses}
        onSelectAll={() => setSelected(new Set(filteredTopLevel.map((i) => i.id)))}
        onClear={() => setSelected(new Set())}
        onBulkStatus={(statusId) =>
          runBulk((id) => update.mutateAsync({ id, payload: { status: statusId } }), 'Status updated')
        }
        onBulkPriority={(prio) =>
          runBulk((id) => update.mutateAsync({ id, payload: { priority: prio as any } }), 'Priority updated')
        }
        onBulkDelete={() => runBulk((id) => remove.mutateAsync(id), 'Issues deleted')}
        busy={bulkBusy}
      />

      {/* New Issue Modal */}
      <NewIssueModal
        open={newIssueOpen}
        onClose={() => setNewIssueOpen(false)}
        spaceId={spaceId}
      />

      {/* Issue Detail Modal */}
      {activeIssue && (
        <IssueModal
          issue={activeIssue}
          open={!!activeIssue}
          onClose={handleCloseIssue}
          statuses={statuses}
          members={allUsers}
          epics={issues.filter((i) => i.type === 'epic')}
          departments={(space as any)?.departments}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}

      {/* Time Tracking Modal */}
      {timeTrackingIssueId && (
        <TimeTrackingModal
          open={timeTrackingOpen}
          onClose={() => {
            setTimeTrackingOpen(false)
            setTimeTrackingIssueId(null)
          }}
          onSave={handleSaveTimeTracking}
          estimatedHours={issues.find((i) => i.id === timeTrackingIssueId)?.estimatedHours}
          initialTimeSpent={issues.find((i) => i.id === timeTrackingIssueId)?.timeSpent || 0}
          initialTimeRemaining={issues.find((i) => i.id === timeTrackingIssueId)?.timeRemaining ?? null}
        />
      )}
    </div>
  )
}
